import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, Receipt, CreditCard, Plus, Trash2, FileText,
  Euro, Loader2, X, Target, Landmark, Calendar, Check, Eye, EyeOff, Shield, ShieldCheck,
  Upload, CheckCircle2, AlertCircle, ArrowUpDown, Download, FileDown,
  ArrowDownRight, ArrowUpRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const EXPENSE_CATEGORIES = [
  { value: 'hosting', label: 'Hosting / Servidor' },
  { value: 'software', label: 'Software / Herramientas' },
  { value: 'marketing', label: 'Marketing / Publicidad' },
  { value: 'formacion', label: 'Formación' },
  { value: 'comisiones_plataforma', label: 'Comisiones plataforma' },
  { value: 'material', label: 'Material' },
  { value: 'profesional', label: 'Servicios profesionales' },
  { value: 'otro', label: 'Otro' },
];

/* ========== FACTURACIÓN TAB ========== */
function TabFacturacion({ invoices, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [batchFrom, setBatchFrom] = useState('');
  const [batchTo, setBatchTo] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [form, setForm] = useState({ client_name: '', client_email: '', client_country: 'ES', client_cp: '', client_nif: '', concept: '', amount: '', payment_method: 'paypal', payment_status: 'paid' });

  const realInvoices = invoices.filter(i => i.amount > 0);
  const totalPaid = realInvoices.filter(i => i.payment_status === 'paid').reduce((s, i) => s + i.amount, 0);

  const handleCreate = async () => {
    if (!form.client_name || !form.concept || !form.amount) { toast.error('Completa nombre, concepto e importe'); return; }
    if (!form.client_country) { toast.error('Selecciona el país del cliente para calcular IVA'); return; }
    try {
      await api.post('/admin/finanzas/facturas', { ...form, amount: parseFloat(form.amount) });
      toast.success('Factura creada');
      setShowModal(false);
      setForm({ client_name: '', client_email: '', client_country: 'ES', client_cp: '', client_nif: '', concept: '', amount: '', payment_method: 'paypal', payment_status: 'paid' });
      onRefresh();
    } catch { toast.error('Error al crear factura'); }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/finanzas/facturas/${id}`);
      toast.success('Factura eliminada');
      onRefresh();
    } catch { toast.error('Error al eliminar'); }
    setDeleting(null);
  };

  const downloadPdf = async (id, num) => {
    setDownloading(id);
    try {
      const res = await api.get(`/admin/finanzas/invoice/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${num || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar'); }
    setDownloading(null);
  };

  const previewPdf = async (id) => {
    setPreviewLoading(id);
    try {
      const res = await api.get(`/admin/finanzas/invoice/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch { toast.error('Error al previsualizar'); }
    setPreviewLoading(null);
  };

  const downloadBatch = async () => {
    if (!batchFrom || !batchTo) { toast.error('Selecciona fecha inicio y fin'); return; }
    setBatchLoading(true);
    try {
      const res = await api.get(`/admin/finanzas/invoices/download-batch?date_from=${batchFrom}&date_to=${batchTo}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = url; a.download = `facturas_${batchFrom}_${batchTo}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Descarga completada');
    } catch { toast.error('No hay facturas en ese periodo'); }
    setBatchLoading(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-[#5a4a2a]/40">Total facturado (pagado)</p>
          <p className="text-2xl font-heading text-[#5a4a2a]">€{fmt(totalPaid)}</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#c9a227] hover:bg-[#b8922a] text-white" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva factura
        </Button>
      </div>

      {/* Batch download */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-[#5a4a2a]/40">Descargar por periodo:</span>
        <input type="date" value={batchFrom} onChange={e => setBatchFrom(e.target.value)} className="text-xs border border-[#e8e2d8] rounded-lg px-2 py-1.5" />
        <span className="text-xs text-[#5a4a2a]/30">a</span>
        <input type="date" value={batchTo} onChange={e => setBatchTo(e.target.value)} className="text-xs border border-[#e8e2d8] rounded-lg px-2 py-1.5" />
        <button onClick={downloadBatch} disabled={batchLoading} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#5a4a2a]/5 text-[#5a4a2a]/60 hover:bg-[#5a4a2a]/10 border border-[#e8e2d8] transition-all" data-testid="batch-download-btn">
          {batchLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
          Descargar ZIP
        </button>
      </div>

      {realInvoices.length === 0 ? (
        <p className="text-center text-[#5a4a2a]/30 py-12 italic">No hay facturas con importe real</p>
      ) : (
        <div className="space-y-2">
          {realInvoices.map(inv => (
            <div key={inv.id} className="flex items-center justify-between bg-white border border-[#e8e2d8] rounded-xl px-4 py-3" data-testid={`invoice-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#5a4a2a]/30 font-mono">#{inv.invoice_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${inv.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                    {inv.payment_status === 'paid' ? 'Pagada' : 'Pendiente'}
                  </span>
                </div>
                <p className="text-sm text-[#5a4a2a] mt-0.5 truncate">{inv.client_name} — {inv.concept}</p>
                <p className="text-xs text-[#5a4a2a]/30">{inv.created_at?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <div className="text-right">
                  <span className="text-lg font-heading text-[#5a4a2a]">€{fmt(inv.total || inv.amount)}</span>
                  {inv.vat_rate > 0 && <p className="text-[9px] text-[#5a4a2a]/30">Base: €{fmt(inv.base_imponible || inv.amount)} + IVA {inv.vat_rate}%</p>}
                  {inv.vat_rate === 0 && inv.vat_legal_note && <p className="text-[9px] text-[#5a4a2a]/30">Sin IVA</p>}
                  {inv.irpf_rate > 0 && <p className="text-[9px] text-rose-400">IRPF -{inv.irpf_rate}% (€{fmt(inv.irpf_amount)})</p>}
                </div>
                <button onClick={() => previewPdf(inv.id)} className="text-[#5a4a2a]/40 hover:text-[#5a4a2a] transition-colors p-1" title="Vista previa" data-testid={`preview-inv-${inv.id}`}>
                  {previewLoading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => downloadPdf(inv.id, inv.invoice_number)} className="text-[#c9a227] hover:text-[#8b6914] transition-colors p-1" title="Descargar PDF" data-testid={`pdf-inv-${inv.id}`}>
                  {downloading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
                <button onClick={() => handleDelete(inv.id)} className="text-rose-400 hover:text-rose-600 transition-colors p-1" data-testid={`delete-inv-${inv.id}`}>
                  {deleting === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="pdf-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col shadow-2xl mx-4">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#e8e2d8]">
              <span className="text-sm font-heading text-[#5a4a2a]">Vista previa de factura</span>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-[#5a4a2a]/40 hover:text-[#5a4a2a] transition-colors" data-testid="close-preview">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full rounded-b-2xl" title="Vista previa factura" />
          </div>
        </div>
      )}

      {/* Modal — Nueva Factura */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg border border-[#e8e2d8]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-[#5a4a2a]">Nueva Factura</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#5a4a2a]/40" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Nombre cliente" value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} data-testid="inv-name" />
              <Input placeholder="Email cliente" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} data-testid="inv-email" />
              <Input placeholder="NIF/CIF cliente (si tiene, se aplica 7% IRPF)" value={form.client_nif} onChange={e => setForm(f => ({ ...f, client_nif: e.target.value }))} data-testid="inv-nif" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.client_country} onChange={e => setForm(f => ({ ...f, client_country: e.target.value }))} className="border border-[#e8e2d8] rounded-lg px-3 py-2 text-sm" data-testid="inv-country">
                  <option value="">— País —</option>
                  <option value="ES">España</option>
                  <option value="FR">Francia</option><option value="DE">Alemania</option><option value="IT">Italia</option>
                  <option value="PT">Portugal</option><option value="GB">Reino Unido</option><option value="MX">México</option>
                  <option value="AR">Argentina</option><option value="CO">Colombia</option><option value="US">EEUU</option>
                  <option value="OTHER">Otro</option>
                </select>
                {form.client_country === 'ES' && (
                  <Input placeholder="Código postal" value={form.client_cp} onChange={e => setForm(f => ({ ...f, client_cp: e.target.value }))} data-testid="inv-cp" />
                )}
              </div>
              <Input placeholder="Concepto" value={form.concept} onChange={e => setForm(f => ({ ...f, concept: e.target.value }))} data-testid="inv-concept" />
              <Input type="number" placeholder="Base imponible (€)" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="inv-amount" />
              <p className="text-[10px] text-[#5a4a2a]/40">El IVA se calculará según país. Si el cliente tiene NIF/CIF, se aplicará retención IRPF 7%.</p>
              <select value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full border border-[#e8e2d8] rounded-lg px-3 py-2 text-sm">
                <option value="paypal">PayPal</option>
                <option value="transferencia">Transferencia</option>
                <option value="bizum">Bizum</option>
                <option value="efectivo">Efectivo</option>
              </select>
              <select value={form.payment_status} onChange={e => setForm(f => ({ ...f, payment_status: e.target.value }))} className="w-full border border-[#e8e2d8] rounded-lg px-3 py-2 text-sm">
                <option value="paid">Pagada</option>
                <option value="pending">Pendiente</option>
              </select>
              <Button onClick={handleCreate} className="w-full bg-[#c9a227] hover:bg-[#b8922a] text-white" data-testid="submit-invoice">Crear Factura</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== GASTOS TAB ========== */
function TabGastos({ expenses, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ vendor: '', concept: '', category: 'otro', amount: '', vat_amount: '0', vat_deductible: true, is_eu_autorepercusion: false, date: new Date().toISOString().slice(0, 10), payment_method: 'transferencia' });

  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const totalDeductible = expenses.filter(e => e.vat_deductible).reduce((s, e) => s + (e.amount || 0), 0);

  const handleCreate = async () => {
    if (!form.vendor || !form.concept || !form.amount) { toast.error('Completa proveedor, concepto e importe'); return; }
    try {
      await api.post('/admin/finanzas/gastos', { ...form, amount: parseFloat(form.amount), vat_amount: parseFloat(form.vat_amount || '0'), receipt_url: '', notes: '' });
      toast.success('Gasto registrado');
      setShowModal(false);
      setForm({ vendor: '', concept: '', category: 'otro', amount: '', vat_amount: '0', vat_deductible: true, is_eu_autorepercusion: false, date: new Date().toISOString().slice(0, 10), payment_method: 'transferencia' });
      onRefresh();
    } catch { toast.error('Error al registrar gasto'); }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/finanzas/gastos/${id}`);
      toast.success('Gasto eliminado');
      onRefresh();
    } catch { toast.error('Error al eliminar'); }
    setDeleting(null);
  };

  const catLabel = (val) => EXPENSE_CATEGORIES.find(c => c.value === val)?.label || val;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-8">
          <div>
            <p className="text-sm text-[#5a4a2a]/40">Total gastos</p>
            <p className="text-2xl font-heading text-[#5a4a2a]">€{fmt(totalExpenses)}</p>
          </div>
          <div>
            <p className="text-sm text-[#5a4a2a]/40">Desgravables</p>
            <p className="text-2xl font-heading text-emerald-700">€{fmt(totalDeductible)}</p>
          </div>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#c9a227] hover:bg-[#b8922a] text-white" data-testid="create-expense-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo gasto
        </Button>
      </div>

      {expenses.length === 0 ? (
        <p className="text-center text-[#5a4a2a]/30 py-12 italic">No hay gastos registrados</p>
      ) : (
        <div className="space-y-2">
          {expenses.map(exp => (
            <div key={exp.id} className="flex items-center justify-between bg-white border border-[#e8e2d8] rounded-xl px-4 py-3" data-testid={`expense-${exp.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#faf8f4] text-[#5a4a2a]/50 border border-[#e8e2d8]">{catLabel(exp.category)}</span>
                  {exp.vat_deductible && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Desgravable</span>}
                  {exp.is_eu_autorepercusion && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Autorepercusión UE</span>}
                </div>
                <p className="text-sm text-[#5a4a2a] mt-0.5 truncate">{exp.vendor} — {exp.concept}</p>
                <p className="text-xs text-[#5a4a2a]/30">{(exp.date || exp.created_at)?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-lg font-heading text-[#5a4a2a]">€{fmt(exp.amount || 0)}</span>
                <button onClick={() => handleDelete(exp.id)} className="text-rose-400 hover:text-rose-600 transition-colors p-1">
                  {deleting === exp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg border border-[#e8e2d8]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-[#5a4a2a]">Nuevo Gasto</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#5a4a2a]/40" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Proveedor" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="exp-vendor" />
              <Input placeholder="Concepto" value={form.concept} onChange={e => setForm(f => ({ ...f, concept: e.target.value }))} data-testid="exp-concept" />
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-[#e8e2d8] rounded-lg px-3 py-2 text-sm" data-testid="exp-category">
                {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <Input type="number" placeholder="Importe (€)" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="exp-amount" />
              <Input type="number" placeholder="IVA soportado (€)" value={form.vat_amount} onChange={e => setForm(f => ({ ...f, vat_amount: e.target.value }))} />
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-[#5a4a2a]/60 cursor-pointer">
                <input type="checkbox" checked={form.vat_deductible} onChange={e => setForm(f => ({ ...f, vat_deductible: e.target.checked }))} className="rounded border-[#e8e2d8]" />
                Gasto desgravable
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5a4a2a]/60 cursor-pointer" data-testid="exp-autorepercusion">
                <input type="checkbox" checked={form.is_eu_autorepercusion} onChange={e => setForm(f => ({ ...f, is_eu_autorepercusion: e.target.checked }))} className="rounded border-[#e8e2d8]" />
                Autorepercusión IVA (SaaS UE: Canva, Notion...)
              </label>
              <Button onClick={handleCreate} className="w-full bg-[#c9a227] hover:bg-[#b8922a] text-white" data-testid="submit-expense">Registrar Gasto</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== VISIÓN FISCAL TAB ========== */
function TabFiscal({ invoices, expenses }) {
  const now = new Date();
  const year = now.getFullYear();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const [sim, setSim] = useState(null);
  const [selectedQ, setSelectedQ] = useState(currentQ);
  const [loadingSim, setLoadingSim] = useState(false);

  const paidInvoices = invoices.filter(i => i.payment_status === 'paid' && (i.base_imponible || i.amount) > 0);

  const byQuarter = (items, dateField, field = 'amount') => {
    const qs = [0, 0, 0, 0];
    items.forEach(item => {
      const d = item[dateField] || item.created_at || '';
      const m = parseInt(d.slice(5, 7));
      if (d.startsWith(String(year)) && m >= 1) qs[Math.floor((m - 1) / 3)] += item[field] || item.amount || 0;
    });
    return qs;
  };

  const revQ = byQuarter(paidInvoices, 'paid_at', 'base_imponible');
  const expQ = byQuarter(expenses, 'date');
  const yearRev = revQ.reduce((a, b) => a + b, 0);
  const yearExp = expQ.reduce((a, b) => a + b, 0);
  const yearProfit = yearRev - yearExp;

  useEffect(() => {
    setLoadingSim(true);
    api.get(`/admin/finanzas/fiscal-simulation?quarter=${selectedQ}&year=${year}`)
      .then(r => setSim(r.data))
      .catch(() => {})
      .finally(() => setLoadingSim(false));
  }, [selectedQ, year]);

  const [goalData, setGoalData] = useState({ annual_goal: 120000 });
  const [editGoal, setEditGoal] = useState(false);
  const [newGoal, setNewGoal] = useState('');

  useEffect(() => {
    api.get('/admin/finanzas/dashboard').then(r => {
      if (r.data?.annual_goal) setGoalData({ annual_goal: r.data.annual_goal });
    }).catch(() => {});
  }, []);

  const saveGoal = async () => {
    try {
      await api.put('/admin/finanzas/config', { annual_goal: parseFloat(newGoal) });
      setGoalData({ annual_goal: parseFloat(newGoal) });
      setEditGoal(false);
      toast.success('Meta actualizada');
    } catch { toast.error('Error'); }
  };

  const goalPct = (yearRev / Math.max(1, goalData.annual_goal)) * 100;
  const m303 = sim?.modelo_303 || {};
  const m130 = sim?.modelo_130 || {};

  return (
    <div className="space-y-6">
      {/* Simulation banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
        <Shield className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          <strong>Modo simulación fiscal</strong> — Estos cálculos son estimaciones informativas. Cuando te des de alta como autónomo, se convertirán en datos fiscales reales.
        </p>
      </div>

      {/* Annual Goal */}
      <div className="bg-white border border-[#e8e2d8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#c9a227]" />
            <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Meta Anual {year}</span>
          </div>
          {!editGoal ? (
            <button onClick={() => { setNewGoal(String(goalData.annual_goal)); setEditGoal(true); }} className="text-xs text-[#c9a227] hover:underline">Editar</button>
          ) : (
            <div className="flex items-center gap-2">
              <Input type="number" value={newGoal} onChange={e => setNewGoal(e.target.value)} className="w-28 h-8 text-sm" />
              <button onClick={saveGoal}><Check className="w-4 h-4 text-emerald-600" /></button>
              <button onClick={() => setEditGoal(false)}><X className="w-4 h-4 text-[#5a4a2a]/30" /></button>
            </div>
          )}
        </div>
        <div className="flex items-end justify-between mb-2">
          <p className="text-2xl font-heading text-[#5a4a2a]">€{fmt(yearRev)} <span className="text-sm text-[#5a4a2a]/30 font-normal">/ €{fmt(goalData.annual_goal)}</span></p>
          <span className="text-sm text-[#c9a227] font-medium">{goalPct.toFixed(1)}%</span>
        </div>
        <div className="h-3 bg-[#e8e2d8] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#c9a227] to-[#e8c547] rounded-full transition-all" style={{ width: `${Math.min(100, goalPct)}%` }} />
        </div>
      </div>

      {/* Quarterly Breakdown */}
      <div className="bg-white border border-[#e8e2d8] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-[#c9a227]" />
          <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Desglose Trimestral</span>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {['T1', 'T2', 'T3', 'T4'].map((label, i) => (
            <button key={i} onClick={() => setSelectedQ(i + 1)}
              className={`text-center p-2 rounded-lg transition-all ${selectedQ === i + 1 ? 'bg-[#c9a227]/10 ring-1 ring-[#c9a227]/30' : 'hover:bg-[#faf8f4]'}`}>
              <p className="text-xs text-[#5a4a2a]/30 mb-1">{label}</p>
              <p className="text-sm font-heading text-[#5a4a2a]">€{fmt(revQ[i])}</p>
              <p className="text-xs text-rose-400">-€{fmt(expQ[i])}</p>
              <div className="w-full h-1 bg-[#e8e2d8] rounded-full mt-1">
                <div className="h-full bg-[#c9a227] rounded-full" style={{ width: `${revQ[i] > 0 ? Math.min(100, ((revQ[i] - expQ[i]) / revQ[i]) * 100) : 0}%` }} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Modelo 303 + 130 Simulation */}
      {loadingSim ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#c9a227]" /></div>
      ) : sim && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Modelo 303 — IVA */}
          <div className="bg-white border border-[#e8e2d8] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-4 h-4 text-[#c9a227]" />
              <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Modelo 303 (IVA) — {sim.quarter_label}</span>
            </div>
            <p className="text-[9px] text-amber-500 mb-3">SIMULACIÓN</p>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">IVA repercutido</span><span className="text-sm text-[#5a4a2a]">€{fmt(m303.iva_repercutido || 0)}</span></div>
              {m303.iva_autorepercusion > 0 && (
                <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50 pl-3 text-xs italic">— incl. autorepercusión UE</span><span className="text-xs text-blue-500">€{fmt(m303.iva_autorepercusion)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">IVA soportado deducible</span><span className="text-sm text-emerald-600">-€{fmt(m303.iva_soportado || 0)}</span></div>
              <div className="border-t border-[#e8e2d8] pt-2 flex justify-between">
                <span className="text-sm font-medium text-[#5a4a2a]">{m303.label || 'Resultado'}</span>
                <span className="text-lg font-heading text-[#5a4a2a]" data-testid="iva-total">€{fmt(m303.resultado || 0)}</span>
              </div>
            </div>
          </div>

          {/* Modelo 130 — IRPF */}
          <div className="bg-white border border-[#e8e2d8] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="w-4 h-4 text-[#c9a227]" />
              <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Modelo 130 (IRPF) — {sim.quarter_label}</span>
            </div>
            <p className="text-[9px] text-amber-500 mb-3">SIMULACIÓN</p>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">Ingresos base</span><span className="text-sm text-[#5a4a2a]">€{fmt(m130.ingresos_base || 0)}</span></div>
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">Gastos deducibles</span><span className="text-sm text-emerald-600">-€{fmt(m130.gastos_deducibles || 0)}</span></div>
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">Beneficio neto</span><span className="text-sm text-[#5a4a2a]">€{fmt(m130.beneficio_neto || 0)}</span></div>
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">Pago fraccionado (20%)</span><span className="text-sm text-[#5a4a2a]">€{fmt(m130.pago_fraccionado_20 || 0)}</span></div>
              <div className="flex justify-between"><span className="text-sm text-[#5a4a2a]/50">Retenciones recibidas</span><span className="text-sm text-emerald-600">-€{fmt(m130.retenciones_recibidas || 0)}</span></div>
              <div className="border-t border-[#e8e2d8] pt-2 flex justify-between">
                <span className="text-sm font-medium text-[#5a4a2a]">Resultado estimado</span>
                <span className="text-lg font-heading text-[#5a4a2a]" data-testid="irpf-total">€{fmt(m130.resultado_estimado || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Net Profit */}
      <div className="bg-[#c9a227]/5 border border-[#c9a227]/20 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-[#c9a227]" />
          <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Beneficio Neto Anual {year}</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="net-profit">€{fmt(Math.max(0, yearProfit))}</p>
            <p className="text-xs text-[#5a4a2a]/40 mt-1">Ingresos - Gastos (antes de impuestos)</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-[#5a4a2a]/50">Margen</p>
            <p className="text-lg font-heading text-[#5a4a2a]">
              {yearRev > 0 ? ((yearProfit / yearRev) * 100).toFixed(1) : '0'}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== RECONCILIACIÓN TAB ========== */
function TabReconciliacion({ onRefresh }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [selIncomes, setSelIncomes] = useState(new Set());
  const [selExpenses, setSelExpenses] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  useEffect(() => {
    api.get('/admin/finanzas/pending-transactions')
      .then(r => setPending(r.data?.pending || []))
      .catch(() => {})
      .finally(() => setLoadingPending(false));
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/finanzas/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      const newInc = res.data.incomes?.new || [];
      const newExp = res.data.expenses?.new || [];
      setSelIncomes(new Set(newInc.map((_, i) => i)));
      setSelExpenses(new Set(newExp.map((_, i) => i)));
      const s = res.data.summary || {};
      toast.success(`CSV procesado: ${s.incomes_new || 0} ingresos + ${s.expenses_new || 0} gastos detectados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar CSV');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const approveSelected = async () => {
    const incNew = result?.incomes?.new || [];
    const expNew = result?.expenses?.new || [];
    const incomes = incNew.filter((_, i) => selIncomes.has(i));
    const expenses = expNew.filter((_, i) => selExpenses.has(i));
    if (!incomes.length && !expenses.length) { toast.error('Selecciona al menos una transacción'); return; }
    setApproving(true);
    try {
      const res = await api.post('/admin/finanzas/reconcile/approve', { incomes, expenses });
      toast.success(`${res.data.created_invoices} facturas + ${res.data.created_expenses} gastos creados`);
      setResult(null);
      onRefresh();
    } catch { toast.error('Error al aprobar transacciones'); }
    setApproving(false);
  };

  const s = result?.summary || {};
  const incNew = result?.incomes?.new || [];
  const incMatched = result?.incomes?.matched || [];
  const expNew = result?.expenses?.new || [];
  const totalNew = incNew.length + expNew.length;

  return (
    <div className="space-y-6">
      {/* Upload zone */}
      <div className="bg-white border-2 border-dashed border-[#e8e2d8] hover:border-[#c9a227]/40 rounded-2xl p-8 text-center transition-all">
        <input ref={fileRef} type="file" accept=".csv" onChange={handleUpload} className="hidden" id="csv-upload" data-testid="csv-upload-input" />
        <label htmlFor="csv-upload" className="cursor-pointer">
          <Upload className="w-8 h-8 text-[#c9a227] mx-auto mb-3" />
          <p className="text-sm text-[#5a4a2a]/70 font-medium">
            {uploading ? 'Analizando transacciones...' : 'Sube un CSV de PayPal o N26'}
          </p>
          <p className="text-xs text-[#5a4a2a]/35 mt-1">
            Detecta automáticamente ingresos, gastos profesionales y comisiones
          </p>
        </label>
        {uploading && <Loader2 className="w-5 h-5 text-[#c9a227] animate-spin mx-auto mt-3" />}
      </div>

      {/* Pendientes de asignar */}
      {!loadingPending && pending.length > 0 && !result && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden" data-testid="pending-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b border-amber-200 bg-amber-100/50">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Pendientes de asignar</span>
              <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{pending.length}</span>
            </div>
            <p className="text-xs text-amber-600">Facturas sin alumna/email vinculado</p>
          </div>
          <div className="divide-y divide-amber-100 max-h-[250px] overflow-y-auto">
            {pending.map(tx => (
              <div key={tx.id} className="flex items-center justify-between px-5 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-600/50 font-mono">#{tx.invoice_number}</span>
                    <span className="text-xs text-amber-500">{(tx.paid_at || tx.created_at)?.slice(0, 10)}</span>
                  </div>
                  <p className="text-sm text-[#5a4a2a] truncate">{tx.client_name} — {tx.concept}</p>
                </div>
                <span className="text-sm font-heading text-amber-700 ml-4">€{fmt(tx.total || tx.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-5">
          {/* Summary bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-[#5a4a2a]/40 flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#c9a227]" />
              Fuente: <strong className="text-[#5a4a2a]/70">{result.source === 'paypal' ? 'PayPal' : 'N26'}</strong>
            </span>
            <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">{s.incomes_new} ingresos nuevos</span>
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{s.expenses_new} gastos profesionales</span>
            {s.incomes_matched > 0 && <span className="text-xs bg-[#faf8f4] text-[#5a4a2a]/40 px-2 py-0.5 rounded-full">{s.incomes_matched} ya registrados</span>}
            {s.incomes_duplicates > 0 && <span className="text-xs bg-[#faf8f4] text-[#5a4a2a]/30 px-2 py-0.5 rounded-full">{s.incomes_duplicates + s.expenses_duplicates} duplicados</span>}
          </div>

          {/* === INCOMES SECTION === */}
          {incNew.length > 0 && (
            <div className="bg-white border border-[#e8e2d8] rounded-2xl overflow-hidden" data-testid="incomes-section">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e8e2d8] bg-emerald-50/30">
                <div className="flex items-center gap-2">
                  <ArrowDownRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-[#5a4a2a]">Ingresos — Facturas a crear</span>
                  <span className="text-xs text-[#5a4a2a]/30">({incNew.length})</span>
                </div>
                <button onClick={() => setSelIncomes(prev => prev.size === incNew.length ? new Set() : new Set(incNew.map((_, i) => i)))}
                  className="text-xs text-[#c9a227] hover:underline" data-testid="select-all-incomes">
                  {selIncomes.size === incNew.length ? 'Deseleccionar' : 'Seleccionar todo'}
                </button>
              </div>
              <div className="divide-y divide-[#e8e2d8]/50 max-h-[350px] overflow-y-auto">
                {incNew.map((tx, idx) => (
                  <label key={idx} className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${selIncomes.has(idx) ? 'bg-emerald-50/30' : 'hover:bg-[#faf8f4]'}`}>
                    <input type="checkbox" checked={selIncomes.has(idx)} onChange={() => {
                      const next = new Set(selIncomes); next.has(idx) ? next.delete(idx) : next.add(idx); setSelIncomes(next);
                    }} className="rounded border-[#e8e2d8] text-emerald-500 focus:ring-emerald-500/30" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[#5a4a2a]/30">{tx.date}</span>
                        {tx.country && <span className="text-[9px] bg-[#faf8f4] text-[#5a4a2a]/40 px-1.5 py-0.5 rounded">{tx.country}</span>}
                        {tx.linked_student && (
                          <span className="text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <CheckCircle2 className="w-2.5 h-2.5" /> {tx.linked_student.name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#5a4a2a] truncate font-medium">{tx.name || 'Sin nombre'}</p>
                      <p className="text-xs text-[#5a4a2a]/40 truncate">{tx.concept}</p>
                      {tx.email && <p className="text-xs text-[#5a4a2a]/25">{tx.email}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-heading text-emerald-700">€{fmt(tx.gross)}</p>
                      {tx.fee > 0 && <p className="text-[10px] text-rose-400">-€{fmt(tx.fee)} comisión</p>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* === EXPENSES SECTION === */}
          {expNew.length > 0 && (
            <div className="bg-white border border-[#e8e2d8] rounded-2xl overflow-hidden" data-testid="expenses-section">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e8e2d8] bg-blue-50/30">
                <div className="flex items-center gap-2">
                  <ArrowUpRight className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-[#5a4a2a]">Gastos profesionales — A registrar</span>
                  <span className="text-xs text-[#5a4a2a]/30">({expNew.length})</span>
                </div>
                <button onClick={() => setSelExpenses(prev => prev.size === expNew.length ? new Set() : new Set(expNew.map((_, i) => i)))}
                  className="text-xs text-[#c9a227] hover:underline" data-testid="select-all-expenses">
                  {selExpenses.size === expNew.length ? 'Deseleccionar' : 'Seleccionar todo'}
                </button>
              </div>
              <div className="divide-y divide-[#e8e2d8]/50 max-h-[350px] overflow-y-auto">
                {expNew.map((tx, idx) => (
                  <label key={idx} className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${selExpenses.has(idx) ? 'bg-blue-50/30' : 'hover:bg-[#faf8f4]'}`}>
                    <input type="checkbox" checked={selExpenses.has(idx)} onChange={() => {
                      const next = new Set(selExpenses); next.has(idx) ? next.delete(idx) : next.add(idx); setSelExpenses(next);
                    }} className="rounded border-[#e8e2d8] text-blue-500 focus:ring-blue-500/30" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[#5a4a2a]/30">{tx.date}</span>
                        <span className="text-[9px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded">{tx.category}</span>
                        {tx.is_eu_autorepercusion && <span className="text-[9px] bg-violet-50 text-violet-500 px-1.5 py-0.5 rounded">Autorepercusión UE</span>}
                      </div>
                      <p className="text-sm text-[#5a4a2a] truncate font-medium">{tx.vendor}</p>
                      {tx.concept && <p className="text-xs text-[#5a4a2a]/40 truncate">{tx.concept}</p>}
                    </div>
                    <p className="text-sm font-heading text-blue-600 flex-shrink-0">€{fmt(tx.amount)}</p>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Already registered (collapsed) */}
          {incMatched.length > 0 && (
            <details className="bg-white border border-emerald-100 rounded-2xl overflow-hidden">
              <summary className="flex items-center gap-2 px-5 py-3 cursor-pointer border-b border-emerald-100 bg-emerald-50/30">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-800">Ya registradas</span>
                <span className="text-xs text-emerald-500">({incMatched.length})</span>
              </summary>
              <div className="divide-y divide-emerald-50 max-h-[200px] overflow-y-auto">
                {incMatched.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between px-5 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#5a4a2a]/50 truncate">{tx.name}</p>
                      <span className="text-xs text-[#5a4a2a]/25">{tx.date}</span>
                    </div>
                    <span className="text-sm text-emerald-600">€{fmt(tx.gross)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* All clear */}
          {totalNew === 0 && incMatched.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-emerald-800 font-medium">Todo al día</p>
              <p className="text-xs text-emerald-600/50 mt-1">Todos los ingresos y gastos ya están registrados</p>
            </div>
          )}

          {/* Approve button */}
          {totalNew > 0 && (
            <div className="flex justify-end">
              <button
                onClick={approveSelected}
                disabled={approving || (selIncomes.size === 0 && selExpenses.size === 0)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#c9a227] text-white hover:bg-[#b8922a] disabled:opacity-40 transition-all shadow-sm"
                data-testid="approve-selected-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Crear {selIncomes.size > 0 ? `${selIncomes.size} facturas` : ''}{selIncomes.size > 0 && selExpenses.size > 0 ? ' + ' : ''}{selExpenses.size > 0 ? `${selExpenses.size} gastos` : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ========== MAIN COMPONENT ========== */
const AdminFinanzas = () => {
  const [activeTab, setActiveTab] = useState('facturacion');
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [studentInvoicesVisible, setStudentInvoicesVisible] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [officialMode, setOfficialMode] = useState(false);
  const [togglingOfficial, setTogglingOfficial] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, expRes, visRes, modeRes] = await Promise.all([
        api.get('/admin/finanzas/facturas'),
        api.get('/admin/finanzas/gastos'),
        api.get('/admin/finanzas/student-invoices-status'),
        api.get('/admin/finanzas/invoicing-mode'),
      ]);
      setInvoices(invRes.data || []);
      setExpenses(expRes.data || []);
      setStudentInvoicesVisible(visRes.data?.visible || false);
      setOfficialMode(modeRes.data?.official || false);
    } catch { toast.error('Error cargando datos'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleStudentInvoices = async () => {
    setTogglingVisibility(true);
    try {
      const res = await api.put('/admin/finanzas/toggle-student-invoices');
      setStudentInvoicesVisible(res.data?.visible || false);
      toast.success(res.data?.visible ? 'Facturas visibles para alumnas' : 'Facturas ocultas para alumnas');
    } catch { toast.error('Error al cambiar visibilidad'); }
    setTogglingVisibility(false);
  };

  const toggleOfficialMode = async () => {
    if (!officialMode) {
      if (!window.confirm('¿Activar facturación OFICIAL?\n\nA partir de ahora las facturas tendrán numeración correlativa oficial (FAC-2026-xxx) con validez fiscal.\n\nLas proformas anteriores se conservan.')) return;
    }
    setTogglingOfficial(true);
    try {
      const res = await api.put('/admin/finanzas/toggle-official-invoicing');
      setOfficialMode(res.data?.official || false);
      toast.success(res.data?.official ? 'Facturación OFICIAL activada' : 'Vuelto a modo PROFORMA');
    } catch { toast.error('Error al cambiar modo'); }
    setTogglingOfficial(false);
  };

  const importHistoricalData = async () => {
    if (!window.confirm('Importar/actualizar datos históricos (Ene-Abr 2026)?\n\n33 facturas proforma (Bizum + PayPal) + 42 gastos.\nSi ya existen datos históricos, se reemplazarán (no se duplican).')) return;
    setImporting(true);
    try {
      const res = await api.post('/admin/finanzas/import-historical-data');
      toast.success(`Importados: ${res.data?.invoices_created} facturas + ${res.data?.expenses_created} gastos`);
      loadData();
    } catch { toast.error('Error al importar datos históricos'); }
    setImporting(false);
  };

  const tabs = [
    { id: 'facturacion', label: 'Facturación', icon: FileText },
    { id: 'gastos', label: 'Gastos', icon: CreditCard },
    { id: 'reconciliacion', label: 'Reconciliación', icon: ArrowUpDown },
    { id: 'fiscal', label: 'Visión Fiscal', icon: Landmark },
  ];

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="admin-finanzas">
      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[#5a4a2a]/40 hover:text-[#5a4a2a] transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-heading text-[#5a4a2a]">Centro Financiero</h1>
              <p className="text-sm text-[#5a4a2a]/40">Facturación, gastos y visión fiscal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={importHistoricalData}
              disabled={importing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border bg-white border-[#e8e2d8] text-[#5a4a2a]/50 hover:border-[#c9a227]/40 transition-all"
              data-testid="import-historical-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Importar histórico
            </button>
            <button
              onClick={toggleOfficialMode}
              disabled={togglingOfficial}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-all ${
                officialMode
                  ? 'bg-[#c9a227]/10 border-[#c9a227]/40 text-[#c9a227] hover:bg-[#c9a227]/20'
                  : 'bg-white border-[#e8e2d8] text-[#5a4a2a]/50 hover:border-[#c9a227]/40'
              }`}
              data-testid="toggle-official-mode"
            >
              {togglingOfficial ? <Loader2 className="w-4 h-4 animate-spin" /> : officialMode ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
              {officialMode ? 'OFICIAL' : 'PROFORMA'}
            </button>
            <button
              onClick={toggleStudentInvoices}
              disabled={togglingVisibility}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-all ${
                studentInvoicesVisible
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-white border-[#e8e2d8] text-[#5a4a2a]/50 hover:border-[#c9a227]/40'
              }`}
              data-testid="toggle-student-invoices"
            >
              {togglingVisibility ? <Loader2 className="w-4 h-4 animate-spin" /> : studentInvoicesVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {studentInvoicesVisible ? 'Alumnas: ON' : 'Alumnas: OFF'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white border border-[#e8e2d8] rounded-xl p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#c9a227] text-white shadow-sm'
                  : 'text-[#5a4a2a]/50 hover:text-[#5a4a2a] hover:bg-[#faf8f4]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#c9a227] animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'facturacion' && <TabFacturacion invoices={invoices} onRefresh={loadData} />}
            {activeTab === 'gastos' && <TabGastos expenses={expenses} onRefresh={loadData} />}
            {activeTab === 'reconciliacion' && <TabReconciliacion onRefresh={loadData} />}
            {activeTab === 'fiscal' && <TabFiscal invoices={invoices} expenses={expenses} />}
          </>
        )}

      </div>
    </div>
  );
};

export default AdminFinanzas;
